"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadBusPortPositionalMergeRule = void 0;
const NetNamingRules_1 = require("../../../../NetCombobulation/NetNamingRules");
const OrcadDsnBusUtils_1 = require("../Generators/OrcadDsnBusUtils");
class OrcadBusPortPositionalMergeRule {
    constructor() {
        this.type = NetNamingRules_1.NetNamingRuleType.Group;
    }
    apply(manager, _hierarchy) {
        for (const busGroup of manager.findGroupsWithObjectsOnDimension('bus')) {
            this.processBusGroup(manager, busGroup);
        }
        return manager;
    }
    processBusGroup(manager, busGroup) {
        for (const [instance, nets] of busGroup.getNets()) {
            const portBusEntries = this.collectPortBusEntries(nets);
            if (portBusEntries.length === 0) {
                continue;
            }
            const busWires = this.collectBusWires(nets);
            if (busWires.length === 0) {
                continue;
            }
            for (const portBus of portBusEntries) {
                const resolvedBus = this.findBusMembersAtPosition(busWires, portBus.position);
                if (!resolvedBus) {
                    continue;
                }
                this.mergePortToBus(manager, instance, portBus, resolvedBus);
            }
        }
    }
    collectPortBusEntries(nets) {
        const results = [];
        for (const net of nets) {
            for (const obj of net.netObjects) {
                const label = obj.label;
                const meta = label?.strategyMetadata;
                const portBus = meta?.['port-bus'];
                const sheetSymbolDbId = meta?.['hierarchical-sheet-entry']?.sheetSymbolDbId;
                if (portBus && sheetSymbolDbId && label) {
                    results.push({
                        members: portBus.members,
                        sheetSymbolDbId,
                        position: label.position,
                    });
                }
            }
        }
        return results;
    }
    collectBusWires(nets) {
        const results = [];
        for (const net of nets) {
            for (const obj of net.netObjects) {
                const wire = obj.wire;
                const bus = wire?.strategyMetadata?.bus;
                if (wire && bus?.members && bus.members.length > 0) {
                    results.push({ members: bus.members, name: bus.name, from: wire.from, to: wire.to });
                }
            }
        }
        return results;
    }
    findBusMembersAtPosition(busWires, position) {
        for (const wire of busWires) {
            const touchesStart = wire.from[0] === position[0] && wire.from[1] === position[1];
            const touchesEnd = wire.to[0] === position[0] && wire.to[1] === position[1];
            if (!touchesStart && !touchesEnd) {
                continue;
            }
            const details = (0, OrcadDsnBusUtils_1.getBusDetails)(wire.name);
            const byIndex = new Map(wire.members.map((m) => [m.index, m]));
            if (details) {
                const ordered = [];
                for (let i = 0; i < details.count; i++) {
                    const member = byIndex.get(details.start + i * details.step);
                    if (member) {
                        ordered.push(member);
                    }
                }
                if (ordered.length === wire.members.length) {
                    return { members: ordered, hasDeclaredRange: true };
                }
            }
            return {
                members: Array.from(wire.members).sort((a, b) => a.index - b.index),
                hasDeclaredRange: details !== undefined,
            };
        }
        return undefined;
    }
    mergePortToBus(manager, instance, portBus, resolvedBus) {
        const busMembers = resolvedBus.members;
        const portLiteralSet = new Set(portBus.members.map((member) => member.name.match(/(\d+)$/)?.[1]));
        const busLiteralSet = new Set(busMembers.map((member) => member.name.match(/(\d+)$/)?.[1]));
        const matchingLiteralSets = !portLiteralSet.has(undefined) &&
            !busLiteralSet.has(undefined) &&
            portLiteralSet.size === busLiteralSet.size &&
            Array.from(portLiteralSet).every((literal) => busLiteralSet.has(literal));
        const pairCount = Math.min(portBus.members.length, busMembers.length);
        const pairs = [];
        for (let i = 0; i < pairCount; i++) {
            const portMember = portBus.members[i];
            const exactName = busMembers.find((candidate) => candidate.name.toUpperCase() === portMember.name.toUpperCase());
            const literalSuffix = portMember.name.match(/(\d+)$/)?.[1];
            const suffixMatches = literalSuffix === undefined ?
                []
                : busMembers.filter((candidate) => candidate.name.match(/(\d+)$/)?.[1] === literalSuffix);
            const busMember = exactName ??
                (!resolvedBus.hasDeclaredRange && matchingLiteralSets && suffixMatches.length === 1 ?
                    suffixMatches[0]
                    : busMembers[i]);
            const sheetEntryGroup = this.findSheetEntryGroup(manager, portMember.name, portBus.sheetSymbolDbId, instance);
            if (!sheetEntryGroup) {
                continue;
            }
            const wireGroup = this.findVirtualBusMemberGroup(manager, busMember.name, instance);
            if (!wireGroup || wireGroup === sheetEntryGroup) {
                continue;
            }
            pairs.push([sheetEntryGroup, wireGroup]);
        }
        for (const [sheetEntryGroup, wireGroup] of pairs) {
            if (manager.resolveGroup(sheetEntryGroup) !== sheetEntryGroup || manager.resolveGroup(wireGroup) !== wireGroup) {
                continue;
            }
            if (sheetEntryGroup === wireGroup) {
                continue;
            }
            manager.mergeGroups(sheetEntryGroup, wireGroup);
        }
    }
    findSheetEntryGroup(manager, memberName, sheetSymbolDbId, instance) {
        const candidates = manager.findGroupsWithLabel('hierarchical-sheet-entry', memberName, instance);
        for (const group of candidates) {
            const match = group
                .getNetLabels(instance)
                .some((label) => label.labelType === 'hierarchical-sheet-entry' &&
                (label.formattedLabel ?? label.originalLabel) === memberName &&
                label.strategyMetadata?.['hierarchical-sheet-entry']?.sheetSymbolDbId === sheetSymbolDbId);
            if (match) {
                return group;
            }
        }
        return undefined;
    }
    findVirtualBusMemberGroup(manager, memberName, instance) {
        const candidates = manager.findGroupsWithLabel('virtual-bus-member', memberName, instance);
        return candidates[0];
    }
}
exports.OrcadBusPortPositionalMergeRule = OrcadBusPortPositionalMergeRule;
//# sourceMappingURL=OrcadBusPortPositionalMergeRule.js.map